import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import LayoutWithSidebar from '../components/LayoutWithSidebar';
import { fetchOrder } from '../services/orderService';
import { toast } from 'react-toastify';
import MapPreview from '../components/MapPreview';

const currencyFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
});

const formatCurrency = (value) => {
  const numeric = Number(value);
  return currencyFormatter.format(Number.isFinite(numeric) ? numeric : 0);
};

export default function OrderDetails() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchOrder(id);
        setOrder(res.data?.data || null);
      } catch (err) {
        toast.error('Failed to load order');
      }
    };
    load();
  }, [id]);

  if (!order) {
    return (
      <LayoutWithSidebar>
        <div className="p-4">Loading...</div>
      </LayoutWithSidebar>
    );
  }

  const mapUniqueExecutives = (executives = []) => {
    const seen = new Set();
    const unique = [];
    executives.forEach((exec) => {
      if (!exec) return;
      const key = exec.id || exec.name;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(exec);
      }
    });
    return unique;
  };

  const orderExecutives = mapUniqueExecutives(
    (order.deliveryAssignments || []).map((assignment) => assignment.deliveryBoy),
  );
  const shopExecutives = mapUniqueExecutives(
    (order.shops || []).map((shop) => shop.deliveryBoy),
  );
  const executiveList = orderExecutives.length ? orderExecutives : shopExecutives;
  const executiveSummary = executiveList
    .map((exec) => `${exec.name}${exec.phone ? ` (${exec.phone})` : ''}`)
    .join(', ');
  const isShopWiseOnly = !orderExecutives.length && shopExecutives.length > 0;

  const payments = Array.isArray(order.payments) ? order.payments : [];
  const totalPaid = payments.reduce(
    (sum, payment) => sum + Number(payment?.amountPaid || 0),
    0,
  );
  const finalAmount =
    Number(order.totalAmount || 0) +
    Number(order.deliveryCharge || 0) -
    Number(order.discount || 0);
  const outstandingAmount = Math.max(finalAmount - totalPaid, 0);
  const address = order.customerAddress || null;
  const parseCoordinate = (value) => {
    if (value === null || value === undefined) return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };
  const addressLatitude = address ? parseCoordinate(address.latitude) : null;
  const addressLongitude = address ? parseCoordinate(address.longitude) : null;
  const hasCoordinates = addressLatitude !== null && addressLongitude !== null;
  const googleMapsUrl = hasCoordinates
    ? `https://www.google.com/maps/search/?api=1&query=${addressLatitude},${addressLongitude}`
    : null;

  return (
    <LayoutWithSidebar>
      <div className="p-4">
        <h1 className="text-xl font-semibold mb-4">Order {order.showId}</h1>
        <div className="space-y-1 mb-4">
          <p>
            <strong>Customer:</strong> {order.customer?.name || ''}
          </p>
          <p>
            <strong>Total:</strong> {formatCurrency(order.totalAmount)}
          </p>
          <p>
            <strong>Delivery Charge:</strong> {formatCurrency(order.deliveryCharge)}
          </p>
          <p>
            <strong>Discount:</strong> {formatCurrency(order.discount)}
          </p>
          <p>
            <strong>Final Amount:</strong> {formatCurrency(finalAmount)}
          </p>
          <p>
            <strong>Total Paid:</strong> {formatCurrency(totalPaid)}
          </p>
          <p>
            <strong>Amount Due:</strong> {formatCurrency(outstandingAmount)}
          </p>
          <p>
            <strong>Status:</strong> {order.currentStatus?.name || ''}
          </p>
          {executiveSummary && (
            <p>
              <strong>Delivery Executives:</strong> {executiveSummary}
              {isShopWiseOnly ? ' (shop-wise)' : ''}
            </p>
          )}
        </div>
        <div className={`grid gap-4 mb-6 ${hasCoordinates ? 'md:grid-cols-2' : ''}`}>
          <div className="border rounded p-4 bg-white dark:bg-gray-800">
            <h2 className="font-semibold mb-2">Delivery Address</h2>
            {address ? (
              <div className="space-y-2 text-sm text-gray-700 dark:text-gray-200">
                {address.label ? (
                  <p>
                    <strong>Label:</strong> {address.label}
                  </p>
                ) : null}
                <div>
                  <p className="font-semibold">Address:</p>
                  <p className="whitespace-pre-line">
                    {address.address || 'Not provided'}
                  </p>
                </div>
                <p>
                  <strong>Latitude:</strong>{' '}
                  {addressLatitude !== null ? addressLatitude : 'Not available'}
                </p>
                <p>
                  <strong>Longitude:</strong>{' '}
                  {addressLongitude !== null ? addressLongitude : 'Not available'}
                </p>
                {googleMapsUrl && (
                  <a
                    href={googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-indigo-600 hover:underline"
                  >
                    View in Google Maps
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-300">
                No delivery address recorded for this order.
              </p>
            )}
          </div>
          {hasCoordinates ? (
            <div className="border rounded p-2 bg-white dark:bg-gray-800">
              <MapPreview lat={addressLatitude} lng={addressLongitude} height={260} />
            </div>
          ) : null}
        </div>
        {order.shops && order.shops.length > 0 && (
          <div className="mb-4">
            <h2 className="font-semibold mb-2">Shops</h2>
            {order.shops.map((s) => (
              <div key={s.id} className="border rounded mb-3 p-2">
                <div className="flex justify-between mb-1">
                  <span className="font-medium">{s.shop?.name || `Shop #${s.shopId}`}</span>
                  <span className="font-medium">Total: {s.shopTotal}</span>
                </div>
                {s.items && s.items.length > 0 && (
                  <div className="space-y-1 mt-2">
                    {s.items.map((it) => (
                      <div key={it.id} className="flex justify-between text-sm">
                        <span>{it.productName}</span>
                        <span>
                          {it.quantity} x {it.unitPrice} = {it.totalPrice}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="text-sm mt-2">
                  <strong>Status:</strong> {s.currentStatus?.name || '-'}
                </div>
                {s.deliveryBoy && (
                  <div className="text-sm mt-2">
                    <strong>Delivery Exec:</strong> {s.deliveryBoy.name}
                    {s.deliveryBoy.phone ? ` (${s.deliveryBoy.phone})` : ''}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <Link
          to={`/orders/${id}/edit`}
          className="text-indigo-600"
        >
          Edit Order
        </Link>
      </div>
    </LayoutWithSidebar>
  );
}
